import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Box, Search, Star, Wine } from "lucide-react";
import usePagination from "@/hooks/useSyncedPagination";
import ExpressFullIcon from "@/assets/icons/express-full.svg?react";
import { ActionDataBo } from "@/types/tableAction";
import { useGetAllExpeditionBo, useGetExpeditionDynamicCountsBo } from "@/api/expedition.api";
import { ExpeditionTypeEnum } from "@/constants/enums/expeditionType.enum";
import { PackagingFilterEnum } from "@/constants/enums/packagingFilter.enum";
import type { DynamicCountsBo, GetAllExpeditionBoDto } from "@/types/api/api-types";
import { UnifiedInput } from "@/components/ui/customs/unified-input";
import { QuickFilterButton } from "@/components/shared/quickFilterButton";
import { ResetButton } from "@/components/shared/resetButton";
import {
  carrierToParams,
  useValidatedExpeditionsFilters,
  type ValidatedExpeditionsFilters,
} from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";
import { dateRangeToParams } from "@/components/backOffice/shared/dateFilter.util";
import { ValidatedExpeditionsModals } from "@/components/backOffice/validatedExpeditions/validatedExpeditionsModals";
import { useGetCustoms } from "@/api/customs.api";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import {
  firstScanAtColumn,
  identifierColumn,
  trackingColumn,
  deliveryStatusBadgeColumn,
  clientColumn,
  clientDestColumn,
  countryColumn,
  btlsVolColumn,
  envoiColumn,
  packagingColumn,
} from "@/components/backOffice/shared/expeditionColumns";
import {
  PackagingStatusBox,
  SHIPPED_TAB_QUERY_FILTER,
  type PackagingTab,
} from "./packagingStatusBox";
import { CarrierFilterButton, useCarrierStaticCounts, type CarrierCounts } from "./carrierFilter";

type Props = {
  tab: PackagingTab;
  onTabChange: (tab: PackagingTab) => void;
};

export const PackagingShippedTab = ({ tab, onTabChange }: Props) => {
  const { t } = useTranslation("boLogistics");
  const [actionData, setActionData] = useState<ActionDataBo | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "firstScanAt", desc: true }]);
  const [pagination, setPagination] = usePagination("packagingShipped");

  const { filters, debouncedSearch, actions } = useValidatedExpeditionsFilters();

  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (!searchParams.has("carrier")) actions.setCarrier("fedex");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateFilter = useMemo(
    () => dateRangeToParams("finalizedAt", filters.finalizedAt),
    [filters.finalizedAt],
  );

  const queryFilter = useMemo<GetAllExpeditionBoDto>(
    () => ({
      ...SHIPPED_TAB_QUERY_FILTER,
      ...dateFilter,
      ...(filters.isExpress ? { isExpress: true } : {}),
      ...(filters.isCustomQuotation ? { quotationType: ExpeditionTypeEnum.CUSTOM_QUOTATION } : {}),
      ...(filters.packagingType ? { packagingType: filters.packagingType } : {}),
      ...(filters.isManyBottles ? { isManyBottles: true } : {}),
      ...carrierToParams(filters.carrier),
      ...(debouncedSearch ? { search: debouncedSearch } : {}),
    }),
    [
      dateFilter,
      filters.isExpress,
      filters.isCustomQuotation,
      filters.packagingType,
      filters.isManyBottles,
      filters.carrier,
      debouncedSearch,
    ],
  );

  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [queryFilter, setPagination]);

  const { data: dynamicCounts } = useGetExpeditionDynamicCountsBo(queryFilter);
  const carrierCounts = useCarrierStaticCounts(SHIPPED_TAB_QUERY_FILTER);

  const { data: expeditions, isLoading: isExpeditionsLoading } = useGetAllExpeditionBo({
    ...queryFilter,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);

  const { data: customs = [] } = useGetCustoms();

  const columns = useMemo(
    () => [
      firstScanAtColumn(),
      identifierColumn(setActionData, { hideDraft: true }),
      envoiColumn(),
      trackingColumn({ dhlTntShipped: "status" }),
      deliveryStatusBadgeColumn(),
      clientColumn(),
      clientDestColumn(),
      countryColumn(customs),
      btlsVolColumn(),
      packagingColumn(),
    ],
    [customs, setActionData],
  );

  return (
    <>
      <div className="flex w-full flex-col justify-between gap-2 md:flex-row md:items-center">
        <PackagingStatusBox tab={tab} onTabChange={onTabChange} />
      </div>
      <div className="flex flex-wrap gap-4">
        <div className="w-72">
          <UnifiedInput
            inputType="text"
            leftElement={<Search />}
            value={filters.search}
            onChange={(e) => actions.setSearch(String(e || ""))}
            className="placeholder:text-[#94A3B8]"
            placeholder={t("packagingFilters.searchPlaceholder")}
          />
        </div>

        <PackagingShippedQuickFilters
          filters={filters}
          actions={actions}
          counts={dynamicCounts}
          carrierCounts={carrierCounts}
        />
      </div>

      <DataTable
        data={expeditionsData}
        columns={columns}
        rowCount={expeditions?.count || 0}
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        autoResetPageIndex={false}
        isLoading={isExpeditionsLoading}
        skeletonRowClassName="h-18"
        rowClassname={() => "h-18 bg-white"}
        headerClassName="bg-gray-300"
        headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
      />
      <ValidatedExpeditionsModals
        actionData={actionData}
        onClose={() => setActionData(null)}
        setActionData={setActionData}
        isDeleted={false}
      />
    </>
  );
};

type QuickFiltersProps = {
  filters: ValidatedExpeditionsFilters;
  actions: ReturnType<typeof useValidatedExpeditionsFilters>["actions"];
  counts?: DynamicCountsBo;
  carrierCounts: CarrierCounts;
};

const PackagingShippedQuickFilters = ({
  filters,
  actions,
  counts,
  carrierCounts,
}: QuickFiltersProps) => {
  const { t } = useTranslation("boLogistics");
  const isWoodBox = filters.packagingType === PackagingFilterEnum.WOODBOX;

  return (
    <div className="flex w-full flex-wrap justify-between gap-3">
      <div className="flex flex-wrap gap-1">
        <QuickFilterButton
          icon={<ExpressFullIcon className="size-5" />}
          label={t("packagingFilters.express")}
          count={counts?.express}
          active={filters.isExpress}
          onClick={actions.toggleExpress}
          colorClass="text-[#3562f8]"
          activeBg="border-[#3562f8] bg-[#3562f8]/10"
        />
        <QuickFilterButton
          icon={<Star className="size-4" />}
          label={t("packagingFilters.customQuotation")}
          count={counts?.customQuotation}
          active={filters.isCustomQuotation}
          onClick={actions.toggleCustomQuotation}
          colorClass="text-purple-500"
          activeBg="border-purple-500 bg-purple-100"
        />
        <QuickFilterButton
          icon={<Box className="size-4" />}
          label={t("packagingFilters.woodBox")}
          count={counts?.woodBox}
          active={isWoodBox}
          onClick={() => actions.setPackagingType(isWoodBox ? null : PackagingFilterEnum.WOODBOX)}
          colorClass="text-orange-900"
          activeBg="border-yellow-800 bg-orange-700/30"
        />
        <QuickFilterButton
          icon={<Wine className="size-4" />}
          label={t("packagingFilters.manyBottles")}
          count={counts?.manyBottles}
          active={filters.isManyBottles}
          onClick={actions.toggleManyBottles}
          colorClass="text-teal-600"
          activeBg="border-teal-500 bg-teal-100"
        />
      </div>

      <div className="flex w-full items-center justify-between gap-1 xl:w-auto xl:justify-start">
        <CarrierFilterButton
          carrier={filters.carrier}
          counts={carrierCounts}
          onSelect={actions.setCarrier}
        />
        <ResetButton
          onReset={() => {
            actions.resetAdvancedFilters();
            actions.setCarrier("fedex");
          }}
        />
      </div>
    </div>
  );
};
